import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Button } from 'antd';
import { EnterOutlined } from '@ant-design/icons';
// FB
import { auth } from '../config/fb';
import {
  createUserWithEmailAndPassword,
  sendEmailVerification
} from 'firebase/auth';
// APIs
import { createUser } from '../apis/user.api';
import { checkUserCacheApi, fetchOrganizationId } from '../apis/admin.api';
import { writeRegistrationErrorToS3Api, writeRegistrationSuccessToS3Api, sendErrorByEmail } from '../apis/logs.api';
import { subscriptionApi } from '../apis/subscription.api';
import { registryApi } from '../apis/registry.api';
// REDUX
import { setReset, setCurrentPage } from '../redux/app.slice';


const Register = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const formRef = useRef(null);

  // Group related state
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    organization: '',
    registerMethod: 'email'
  });
  
  const [formState, setFormState] = useState({
    valid: false,
    isReady: false,
    isTyping: false,
    errorMessage: null,
    passwordChecks: {
      minLength: false,
      hasUpperCase: false,
      hasLowerCase: false,
      hasNumber: false,
      hasSpecialChar: false
    }
  });

  const [queuedUser, setQueuedUser] = useState(null);
  const [isReady, setIsReady] = useState(false);
  const [loading, setLoading] = useState(false);

  const consoleHistory = useRef([]);
  
  const { reset } = useSelector((state) => state.app, shallowEqual);

  // Enhanced input handler to trigger validation
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    // console.log(`Input changed: ${name} = ${value}`); // Debug log

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Immediately validate form after input change
    if (name === 'password') {
      const passwordChecks = {
        minLength: value.length >= 6,
        hasUpperCase: /[A-Z]/.test(value),
        hasLowerCase: /[a-z]/.test(value),
        hasNumber: /\d/.test(value),
        hasSpecialChar: /[@$!%*?&]/.test(value)
      };

      const validPassword = Object.values(passwordChecks).every(check => check);
      const validEmail = !!formData.email.match(
        /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      );

      setFormState(prev => ({
        ...prev,
        isTyping: true,
        passwordChecks,
        valid: validEmail && validPassword // Update valid state here
      }));
    } else if (name === 'email') {
      const validEmail = !!value.match(
        /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      );
      const validPassword = Object.values(formState.passwordChecks).every(check => check);

      console.log('Validation state:', { validEmail, validPassword }); // Debug log

      setFormState(prev => ({
        ...prev,
        isTyping: true,
        valid: validEmail && validPassword // Update valid state here
      }));
    }
  };

  // Separate password validation function
  const validatePassword = useCallback((password) => {
    const checks = {
      minLength: password.length >= 6,
      hasUpperCase: /[A-Z]/.test(password),
      hasLowerCase: /[a-z]/.test(password),
      hasNumber: /\d/.test(password),
      hasSpecialChar: /[@$!%*?&]/.test(password)
    };
    return checks;
  }, []);

  // Consolidated form validation
  const validateForm = useCallback(() => {
    const validEmail = !!formData.email.match(
      /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    );
    
    const passwordChecks = validatePassword(formData.password);
    const validPassword = Object.values(passwordChecks).every(check => check);
    
    console.log('Form validation:', { validEmail, validPassword, passwordChecks }); // Debug log
    
    return validEmail && validPassword;
  }, [formData.email, formData.password, validatePassword]);

  // Effect to update validation state when typing stops
  useEffect(() => {
    const timer = setTimeout(() => {
      if (formState.isTyping) {
        const isValid = validateForm();
        console.log('Updating form validity:', isValid); // Debug log
        
        setFormState(prev => ({
          ...prev,
          isTyping: false,
          valid: isValid
        }));
      }
    }, 300); // Debounce time

    return () => clearTimeout(timer);
  }, [formData, formState.isTyping, validateForm]);

  // Simplified error handling
  const handleError = async (message, error) => {
    setFormState(prev => ({
      ...prev,
      errorMessage: message
    }));

    const frontendLogs = consoleHistory.current;

    try {
      await writeRegistrationErrorToS3Api({
        email: formData.email,
        error: {
          message,
          resErr: error,
          frontendLogs
        }
      });
    } catch (err) {
      console.log("Error logging failed:", err);
    }

    // Do not send email when the error is user already registered
    if (error?.name == 'FirebaseError' && error?.code == 'auth/email-already-in-use') return;
    try {
      const errorLog = { message, resErr: error, frontendLogs };
      const emailData = {
        subject: "Registration Error " + formData.email,
        html: `
          <p><strong>User:</strong> ${formData.email}</p>
          <p><strong>Message:</strong> ${message}</p>
          <p><strong>Error:</strong></p>
          <pre>${JSON.stringify(errorLog, null, 2)}</pre>
          `
      };
      await sendErrorByEmail(emailData);
    } catch (err) {
      console.log("sendErrorByEmail failure:", err);
    }
  };

  // Firebase user creation
  const createFirebaseUser = async (authorized) => {
    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth, 
        formData.email, 
        formData.password
      );
      
      const idTokenResult = await userCredential.user.getIdTokenResult();
      const orgRes = await fetchOrganizationId(authorized.organization);
      
      const enrichedUser = {
        ...userCredential.user,
        organization: authorized.organization,
        emailVerified: true,
        name: authorized.name
      };

      await createDbUser(idTokenResult, enrichedUser, orgRes.data);

      // TODO: Migrate user(s) to Registry
      // Add to Subscription if not already..
      // -- Update terms {planType, maxUsers, autoRenew}

      return true;
    } catch (err) {
      if (err.code === "auth/email-already-in-use") {
        await handleError(
          "This user is already registered, please try logging in or contact support@lavenirai.com errorCode=R2",
          err
        );
      }
      return false;
    }
  };

  // DB user creation
  const createDbUser = async (idTokenResult, user, orgData) => {
    try {
        await createUser(idTokenResult, user, orgData);
        
        // Add registry migration before subscription
        let registryRes;
        try {
            console.log('Migrating organization to registry:', user.organization);
            registryRes = await registryApi.migrateOrganizations(idTokenResult.token, {
                organizations: [user.organization],
                db: process.env.REACT_APP_DEPLOY_SERVER || 'dev',
                region: process.env.REACT_APP_REGION_CODE || 'us-east-1',
                filterKey: 'name',
                filterValue: user.organization
            });
            console.log('--> Registry response:', registryRes);
        } catch (regErr) {
            console.error('Failed to migrate organization to registry:', regErr);
            await handleError(
                "Failed to migrate organization, please contact support@lavenirai.com errorCode=R6",
                regErr
            );
            throw regErr;
        }

        // Add user to subscription after DB user creation
        console.log('Adding user to subscription:', user.email, user.organization);
        try {
            const subscriptionRes = await subscriptionApi.addUserToSubscription(
                idTokenResult.token,
                user.organization,
                user.email
            );
            console.log('Subscription response:', subscriptionRes);

            // Now get subscription details to determine status
            console.log('Fetching subscription details for:', user.organization);
            const subDetails = await subscriptionApi.getSubscriptionDetails(
                idTokenResult.token, 
                user.organization
            );
            console.log('Subscription details:', subDetails);

            // Check if this is the first user (no users at all)
            if (subDetails.users.total === 1) {
                console.log('First user detected, setting as organization leader');
                
                // Set this user as the organization leader using the subscription API
                await subscriptionApi.changeSubscriptionLeaderByEmail(
                    idTokenResult.token,
                    user.organization,
                    user.email
                );
                console.log('Set user as organization leader');
            }

            const { planType } = subDetails.subscription.details;
            const { maxUsers } = subDetails.subscription.features;
            const activeUsers = subDetails.users.active.count;
            console.log('Active users:', activeUsers);
            console.log('Plan type:', planType);
            console.log('Max users:', maxUsers);

            // Determine and update status based on plan type and user count
            const newStatus = (planType === 'enterprise' || planType === 'explorer') && 
                            activeUsers <= maxUsers ? 'active' : 'inactive';
            
            await subscriptionApi.updateUserSubscriptionStatus(
                idTokenResult.token,
                user.organization,
                user.email,
                newStatus
            );
            console.log(`Updated subscription status for ${user.email} to ${newStatus}`);

        } catch (subErr) {
            console.error('Failed to add user to subscription:', subErr);
            await handleError(
                "Failed to add user to subscription, please contact support@lavenirai.com errorCode=R5",
                subErr
            );
            throw subErr;
        }
        
        if (process.env.REACT_APP_SUCCESS_LOGGING === "true") {
            await writeRegistrationSuccessToS3Api(idTokenResult, { email: formData.email });
        }
        
        navigate('/login');
    } catch (err) {
        await handleError(
            "Create user failed, please contact support@lavenirai.com errorCode=R3",
            err
        );
        throw err;
    }
  };

  // Main submit handler
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (loading) return;
    setLoading(true);

    // temporarily override console.log and console.error to store the messages in a buffe
    const originalLog = console.log;
    const originalError = console.error;
    console.log = (...args) => {
      consoleHistory.current.push(args);
      originalLog(...args);
    };
    console.error = (...args) => {
      consoleHistory.current.push(args);
      originalError(...args);
    };
    
    try {
      const authorized = await checkIfUserIsInQueue();
      
      if (authorized?.email.toLowerCase() === formData.email.toLowerCase()) {
        const success = await createFirebaseUser(authorized);
        if (success) {
          const enrichedUser = {
            ...userCredential.user,
            organization: authorized.organization,
            emailVerified: true,
            name: authorized.name,
            registerMethod: formData.registerMethod
          };

          await createDbUser(idTokenResult, enrichedUser, orgRes.data);
          setFormData({ email: '', password: '', organization: '', registerMethod: 'email' });
        }
      } else {
        await handleError(
          "This email is not authorized, please contact support@lavenirai.com or your team lead errorCode=R1",
          null
        );
      }
    } catch (err) {
      await handleError(
        "This email is not authorized, please contact support@lavenirai.com errorCode=R4",
        err
      );
    } finally {
      setLoading(false);
      console.log = originalLog;
      console.error = originalError;
      consoleHistory.current = [];
    }
  };

  // Effects
  useEffect(() => {
    dispatch(setCurrentPage('register'));
    const timer = setTimeout(() => {
      setIsReady(true);
    }, 0); // 100ms delay, adjust as needed

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setFormState(prev => ({
        ...prev,
        isTyping: false
      }));
    }, 100);
    return () => clearTimeout(timer);
  }, [formData.email, formData.password]);

  useEffect(() => {
    if (formState.isReady && !formState.isTyping) {
      setFormState(prev => ({
        ...prev,
        valid: validateForm()
      }));
    }
  }, [formData, formState.isReady, formState.isTyping, validateForm]);

  useEffect(() => {
    if (reset) {
      setFormData({ email: '', password: '', organization: '', registerMethod: 'email' });
      setFormState(prev => ({
        ...prev,
        errorMessage: null
      }));
      dispatch(setReset(false));
    }
  }, [reset, dispatch]);

  useEffect(() => {
    if (!formState.passwordChecks) {
      setFormState(prev => ({
        ...prev,
        passwordChecks: {
          minLength: false,
          hasUpperCase: false,
          hasLowerCase: false,
          hasNumber: false,
          hasSpecialChar: false
        }
      }));
    }
  }, []);

  const checkIfUserIsInQueue = async () => {
    const cacheRes = await checkUserCacheApi(formData.email.toLowerCase());
    setQueuedUser(cacheRes.data);
    return cacheRes.data;
  };

  // Updated password requirements display
  const renderPasswordRequirements = () => {
    if (!formData.password) return null;

    const requirements = [
      { label: 'Minimum 6 characters', check: 'minLength' },
      { label: 'One uppercase letter', check: 'hasUpperCase' },
      { label: 'One lowercase letter', check: 'hasLowerCase' },
      { label: 'One number', check: 'hasNumber' },
      { label: 'One special character (@$!%*?&)', check: 'hasSpecialChar' }
    ];

    return (
      <div className="p-2 bg-blue-100 border border-blue-400 text-blue-700 rounded-md text-sm">
        <p className="font-semibold mb-1">Password requirements:</p>
        <ul className="list-none space-y-0.5">
          {requirements.map(({ label, check }) => (
            <li key={check} className="flex items-center">
              <span className={`mr-2 ${formState.passwordChecks[check] ? 'text-green-600' : 'text-red-600'}`}>
                {formState.passwordChecks[check] ? '✓' : '×'}
              </span>
              {label}
            </li>
          ))}
        </ul>
      </div>
    );
  };

  const renderErrorMessage = () => {
    if (!formState.errorMessage) return null;

    return (
      <div className="p-3 bg-red-100 border border-red-400 text-red-700 rounded-md">
        {formState.errorMessage}
      </div>
    );
  };

  const renderForm = () => {
    return (
      <form 
        ref={formRef} 
        onSubmit={handleSubmit} 
        className="space-y-3"
      >
        <div className="space-y-2">
          <input
            type="email"
            className="w-full px-3 py-1.5 border border-gray-300 rounded-md 
                     focus:outline-none focus:ring-2 focus:ring-[#6c63ff]"
            value={formData.email}
            onChange={handleInputChange}
            name="email"
            placeholder="Enter your email"
            autoFocus
            data-testid="email-input"
          />
          <input
            type="password"
            className="w-full px-3 py-1.5 border border-gray-300 rounded-md 
                     focus:outline-none focus:ring-2 focus:ring-[#6c63ff]"
            value={formData.password}
            onChange={handleInputChange}
            name="password"
            placeholder="Enter your password"
            data-testid="password-input"
          />
        </div>

        <button
          type="submit"
          className={`w-full py-1.5 px-4 text-white rounded-md transition-colors 
                    ${formState.valid && !loading
                      ? 'bg-[#6c63ff] hover:bg-[#5b54e0] cursor-pointer' 
                      : 'bg-gray-400 cursor-not-allowed'}`}
          disabled={!formState.valid || loading}
          data-testid="register-button"
        >
          Register
        </button>
      </form>
    );
  };

  return (
    <div className="login-background min-h-screen bg-gray-40 flex items-start 
                    justify-center pt-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-4 bg-white p-6 rounded-xl shadow-md">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-700">
            Register for an account
          </h2>
          {false && process.env.REACT_APP_ENV !== 'production' && (
            <p className="mt-1 text-sm text-gray-500">
              Environment: {process.env.REACT_APP_ENV}
            </p>
          )}
        </div>

        {renderForm()}
        {renderErrorMessage()}
        {renderPasswordRequirements()}

        <div className="mt-2 text-center">
          <Link 
            to="/login"
            className="text-sm text-[#6c63ff] hover:text-[#5b54e0]"
          >
            Already have an account? Login
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Register;
